/**
 * Pure rules for editing order_lines.quantity from the prescription edit
 * dialog. Quantity multiplies unit price, so these are money rules:
 *
 * - Bounds come from the SDK (ONE home: the same constants the SDK write
 *   path validates), min 1, ceiling 10, integers only. The database has
 *   no CHECK on quantity (verified live 2026-09-03), so the UI + SDK are
 *   the only enforcement on the honest path.
 * - The invoiced/paid boundary: once an order is invoiced or paid, a
 *   quantity correction goes through a credit note, never an edit. Keyed
 *   on orders.zoho_invoice_id and client_payment_status in ('invoiced',
 *   'paid'). Hand-made Zoho invoices currently leave BOTH null/unbilled,
 *   so this boundary protects what the platform can see; the durable
 *   DB-level freeze is a backlog item blocked on invoice stamping (see
 *   docs/housekeeping-backlog.md).
 * - The lab-sheet warning: there is NO stored marker for "Excel sent" or
 *   "Gmail draft created" (gmail-create-draft writes nothing), so the
 *   warning keys on LINE STATUS: from assigned_to_provider onward the
 *   sheet is presumed with the manufacturer and a quantity change means
 *   it must be regenerated and resent. Non-blocking by design.
 */
import { orderMutations } from '@insolech/sdk';

export const QUANTITY_MIN = orderMutations.ORDER_LINE_QUANTITY_MIN;
export const QUANTITY_MAX = orderMutations.ORDER_LINE_QUANTITY_MAX;

/** Clamp a step/typed value to a legal quantity. Non-finite input pins to
 *  the minimum rather than guessing. */
export function clampQuantity(n: number): number {
  if (!Number.isFinite(n)) return QUANTITY_MIN;
  return Math.min(Math.max(Math.trunc(n), QUANTITY_MIN), QUANTITY_MAX);
}

/**
 * Parse the text input. Digits-only (the PatientForm "Units (pairs)"
 * precedent): strips every non-digit, so no minus sign and no decimal
 * point can survive. Empty (or digit-free) text returns null, meaning
 * "not a quantity yet"; the dialog disables Save on null rather than
 * silently coercing.
 */
export function parseQuantityInput(text: string): number | null {
  const digits = text.replace(/\D/g, '');
  if (!digits) return null;
  return clampQuantity(parseInt(digits, 10));
}

/**
 * The invoiced/paid boundary. Returns the user-facing reason the
 * quantity control is locked, or null when editing is allowed.
 */
export function quantityEditBlockedReason(order: {
  zoho_invoice_id: string | null;
  client_payment_status: string;
}): string | null {
  if (order.zoho_invoice_id || order.client_payment_status === 'invoiced') {
    return 'This order has been invoiced. A quantity correction now goes through a credit note, not an edit; contact INSOLECH admin.';
  }
  if (order.client_payment_status === 'paid') {
    return 'This order has already been paid. A quantity correction now goes through a credit note or refund, not an edit; contact INSOLECH admin.';
  }
  return null;
}

/** Line statuses where the lab sheet is presumed already with the
 *  manufacturer. on_hold is deliberately excluded (it can occur before
 *  assignment); cancelled is excluded (nothing is being made). */
const LAB_SHEET_OUT_STATUSES: ReadonlySet<string> = new Set([
  'assigned_to_provider',
  'in_production',
  'quality_check',
  'in_delivery',
  'delivered',
  'returned',
]);

export function labSheetAlreadyOut(lineStatus: string): boolean {
  return LAB_SHEET_OUT_STATUSES.has(lineStatus);
}

/** Non-blocking warning shown when quantity changes on a line whose lab
 *  sheet already went out. oldQuantity is what the manufacturer has. */
export function labSheetStaleWarning(oldQuantity: number): string {
  return (
    `This line is already with the manufacturer, and the lab sheet that went out shows the old quantity (${oldQuantity}). ` +
    'Saving updates the order only: regenerate the Excel and resend the supplier draft email, or the wrong number of pairs will be made.'
  );
}

/**
 * Maps a save error to the loud stale-totals message, or null for any
 * other error. The dialog must NEVER close green on this one: the line
 * saved but orders.subtotal/tax/total did not recompute, so every
 * surface reading total_aed (billing card, Stripe checkout mint,
 * payment-mismatch check) is wrong until a retry succeeds.
 */
export function recomputeFailureMessage(e: unknown): string | null {
  if (e instanceof orderMutations.TotalsRecomputeFailedError) {
    return (
      'The quantity WAS saved, but recomputing the order totals failed, so the order total is stale on every surface that shows it. ' +
      'Press "Update prescription" again to retry the recompute. Do not close this dialog until it succeeds; if it keeps failing, contact INSOLECH admin. ' +
      `(${e.message})`
    );
  }
  return null;
}
